// Штуки: именованный вес поверх граммов.
//
// Граммы — единственная правда в базе и расчётах; единица — это «1 шт = N г».
// Источник один — закрытый набор BUILTIN_UNITS (решение владельца 26.10.2026:
// штуки нужны там, где человек в жизни считает штуками, а не граммами; у всех
// остальных продуктов — только граммы). «Свои порции для любого продукта»
// из интерфейса убраны как лишнее усложнение; таблица zoya_user_units и API
// остались ради выбранных размеров (яйцо С1, банан средний, котлета крупная).
//
// Веса — типичные справочные: ГОСТ 31654 для яиц, средние порции Скурихина и
// USDA с поправкой на съедобную часть для фруктов, ГОСТ на сосиски, сборники
// рецептур для котлет и выпечки, упаковки массовых марок для сладостей.
// Пельмень 20 г — варёный, по решению владельца. Размеры есть там, где разброс
// веса больше полутора раз; остальное одной цифрой, граммы правятся рукой.

const one = (grams, label = 'обычный') => ({ defaultSize: 'std', sizes: [{ code: 'std', label, grams }] })

export const BUILTIN_UNITS = {
  // ── Яйца ──
  egg: {
    label: 'Яйцо', unit: 'шт', defaultSize: 'C1',
    // Съедобный вес, без скорлупы: категория по ГОСТ задаёт вес со скорлупой
    // (СВ ≥ 75, С0 65–75, С1 55–65, С2 45–55), скорлупа ≈ 12 %.
    sizes: [
      { code: 'CV', label: 'СВ · отборное', grams: 68 },
      { code: 'C0', label: 'С0 · крупное', grams: 62 },
      { code: 'C1', label: 'С1 · среднее', grams: 53 },
      { code: 'C2', label: 'С2 · мелкое', grams: 44 },
    ],
    // Перепелиные, белок, желток и порошок — не яйцо-штука в этом смысле
    matches: (n) => /^яйц[оа](\s|,|$)/i.test(n) && !/перепел|порош|белок|желток|меланж/i.test(n),
  },
  // ── Фрукты (без кожуры и косточек) ──
  banana: {
    label: 'Банан', unit: 'шт', defaultSize: 'medium',
    sizes: [
      { code: 'small', label: 'небольшой', grams: 80 },
      { code: 'medium', label: 'средний', grams: 110 },
      { code: 'large', label: 'большой', grams: 140 },
    ],
    matches: (n) => /^банан/i.test(n) && !/чипс|сушен|вялен|пюре|сок|молок|хлеб|кекс|мук|смузи|йогурт|мороженое|коктейл/i.test(n),
  },
  apple:     { label: 'Яблоко', unit: 'шт', ...one(130, 'среднее'),   matches: (n) => /^яблок[оаи]/i.test(n) && !/сок|пюре|сушен|чипс|пирог|шарлотк|уксус|джем|повидл|варень|запеч|печён/i.test(n) },
  orange:    { label: 'Апельсин', unit: 'шт', ...one(130, 'средний'), matches: (n) => /^апельсин/i.test(n) && !/сок|цедр|джем|фреш|сушен/i.test(n) },
  tangerine: { label: 'Мандарин', unit: 'шт', ...one(55, 'средний'),  matches: (n) => /^мандарин/i.test(n) && !/сок|цедр|сушен|джем/i.test(n) },
  pear:      { label: 'Груша', unit: 'шт', ...one(140, 'средняя'),    matches: (n) => /^груш[аи]/i.test(n) && !/сок|сушен|варень|пюре|джем|запеч/i.test(n) },
  peach:     { label: 'Персик', unit: 'шт', ...one(120, 'средний'),   matches: (n) => /^(персик|нектарин)/i.test(n) && !/консерв|сок|пюре|джем|сушен|варень/i.test(n) },
  apricot:   { label: 'Абрикос', unit: 'шт', ...one(30),              matches: (n) => /^абрикос/i.test(n) && !/кураг|сушен|сок|джем|варень|консерв|пюре/i.test(n) },
  plum:      { label: 'Слива', unit: 'шт', ...one(30),                matches: (n) => /^слив[аы](\s|,|$)/i.test(n) && !/сливоч|сливк|чернослив|сок|варень|джем|пюре|сушен/i.test(n) },
  kiwi:      { label: 'Киви', unit: 'шт', ...one(65),                 matches: (n) => /^киви/i.test(n) && !/сок|сушен|пюре|джем/i.test(n) },
  avocado:   { label: 'Авокадо', unit: 'шт', ...one(120, 'мякоть'),   matches: (n) => /^авокадо/i.test(n) && !/масл|пюре|паст/i.test(n) },
  // ── Хлеб и выпечка ──
  bread:     { label: 'Хлеб', unit: 'ломтик', ...one(30, 'ломтик'),   matches: (n) => /^хлеб(\s|,|$)/i.test(n) && !/хлебц|крошк|лаваш|сухар/i.test(n) },
  toast:     { label: 'Тост', unit: 'шт', ...one(25),                 matches: (n) => /^тост(ы)?(\s|,|$)/i.test(n) && !/(^|\s)с(\s|$)/i.test(n) },
  crispbread:{ label: 'Хлебец', unit: 'шт', ...one(10),               matches: (n) => /^хлебц/i.test(n) },
  pancake:   { label: 'Блин', unit: 'шт', ...one(45),                 matches: (n) => /^блин/i.test(n) },
  oladya:    { label: 'Оладья', unit: 'шт', ...one(30),               matches: (n) => /^олад/i.test(n) },
  syrnik:    { label: 'Сырник', unit: 'шт', ...one(60),               matches: (n) => /^сырник/i.test(n) },
  // ── Штучная еда ──
  sausage:   { label: 'Сосиска', unit: 'шт', ...one(50),              matches: (n) => /^сосис/i.test(n) && !/в тесте/i.test(n) },
  cutlet:    {
    label: 'Котлета', unit: 'шт', defaultSize: 'std',
    sizes: [
      { code: 'small', label: 'небольшая', grams: 60 },
      { code: 'std', label: 'обычная', grams: 80 },
      { code: 'large', label: 'крупная', grams: 110 },
    ],
    matches: (n) => /^котлет/i.test(n),
  },
  pelmen:    { label: 'Пельмень', unit: 'шт', ...one(20),             matches: (n) => /^пельмен/i.test(n) },
  roll:      { label: 'Ролл', unit: 'шт', ...one(35, 'кусок'),        matches: (n) => /^ролл/i.test(n) && !/кориц|синабон|сладк/i.test(n) },
  cookie:    {
    label: 'Печенье', unit: 'шт', defaultSize: 'std',
    sizes: [
      { code: 'small', label: 'крекер', grams: 5 },
      { code: 'std', label: 'обычное', grams: 12 },
      { code: 'large', label: 'овсяное', grams: 20 },
    ],
    // «Печенье», «Печенья» — да; «Печень куриная» (субпродукт) — нет
    matches: (n) => /^печень[еяи]/i.test(n),
  },
  candy:     { label: 'Конфета', unit: 'шт', ...one(12),              matches: (n) => /^конфет/i.test(n) },
  waffle:    { label: 'Вафля', unit: 'шт', ...one(25),                matches: (n) => /^вафл/i.test(n) },
  gingerbread:{ label: 'Пряник', unit: 'шт', ...one(35),              matches: (n) => /^пряник/i.test(n) },
  zefir:     { label: 'Зефир', unit: 'шт', ...one(30),                matches: (n) => /^зефир/i.test(n) },
  date:      { label: 'Финик', unit: 'шт', ...one(8),                 matches: (n) => /^финик/i.test(n) },
  driedApricot:{ label: 'Курага', unit: 'шт', ...one(7),              matches: (n) => /^кураг/i.test(n) },
  prune:     { label: 'Чернослив', unit: 'шт', ...one(8),             matches: (n) => /^чернослив/i.test(n) },
  walnut:    { label: 'Грецкий орех', unit: 'шт', ...one(5, 'ядро'),  matches: (n) => /^(грецк\S*\s+орех|орех\S*\s+грецк)/i.test(n) && !/масл|мук/i.test(n) },
  almond:    { label: 'Миндаль', unit: 'шт', ...one(1.2),             matches: (n) => /^миндал/i.test(n) && !/мук|молок|паст|лепест|масл/i.test(n) },
}

export function matchBuiltin(name) {
  const n = String(name || '').trim()
  if (!n) return null
  for (const [key, def] of Object.entries(BUILTIN_UNITS)) {
    if (def.matches(n)) return key
  }
  return null
}

export function builtinSize(key, code) {
  const def = BUILTIN_UNITS[key]
  if (!def) return null
  return def.sizes.find(s => s.code === code) || def.sizes.find(s => s.code === def.defaultSize)
}

/**
 * Единица для позиции — только из набора BUILTIN_UNITS. personal — выбранные
 * размеры (byKey: Map(builtin_key → row)); личные порции по продукту больше не
 * применяются. Возвращает { name, grams, kind: 'builtin', key, sizeCode } или null.
 */
export function unitFor(item, personal) {
  const key = matchBuiltin(item?.name || item?.product_name || item?.db_name)
  if (!key) return null
  const pref = personal?.byKey?.get(key)
  const size = builtinSize(key, pref?.size_code)
  return { name: BUILTIN_UNITS[key].unit, grams: size.grams, kind: 'builtin', key, sizeCode: size.code }
}

// Количество в единицах из граммов — с одним знаком, чтобы 110 г при 53 г/шт
// читались как «2.1 шт», а не как «2» с молчаливой потерей 4 г.
export function qtyFromGrams(grams, unitGrams) {
  if (!(unitGrams > 0) || !(grams >= 0)) return null
  return Math.round(grams / unitGrams * 10) / 10
}

export function gramsFromQty(qty, unitGrams) {
  if (!(unitGrams > 0) || !(qty >= 0)) return null
  return Math.round(qty * unitGrams * 10) / 10
}

export function fmtQty(q) {
  if (q == null || Number.isNaN(Number(q))) return ''
  const n = Math.round(Number(q) * 10) / 10
  return Number.isInteger(n) ? String(n) : String(n).replace('.', ',')
}

// Подпись «2 шт · 106 г»; для граммов — просто «106 г»
export function fmtQtyUnit(qty, unitName, grams) {
  if (qty != null && unitName) return `${fmtQty(qty)} ${unitName} · ${Math.round(grams)} г`
  return `${Math.round(grams)} г`
}

// Позиции, собранные из раскладки рецепта, не несут тройку «сколько × в чём»:
// она лежит на записях дневника. Переносим по product_id, иначе по названию,
// чтобы правка и повтор блюда открывались с теми же штуками, что были введены.
export function mergeEntryUnits(items, entries) {
  if (!Array.isArray(items) || !Array.isArray(entries)) return items
  const norm = s => String(s || '').trim().toLowerCase()
  for (const item of items) {
    if (item.unit_name && item.unit_g > 0) continue
    const e = entries.find(x => x.unit_name && x.unit_g > 0 && (
      (item.product_id && x.product_id && Number(x.product_id) === Number(item.product_id)) ||
      norm(x.product_name) === norm(item.name)))
    if (!e) continue
    item.unit_name = e.unit_name
    item.unit_g = parseFloat(e.unit_g)
    item.qty = e.qty != null ? parseFloat(e.qty) : null
  }
  return items
}
